import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarClock, ShieldAlert, AlertCircle, Car, Copy } from 'lucide-react'
import {
  useGetDuplicateSlots,
  useGetVehicleMismatches,
  useGetVehicleSlotClashes,
  useGetKmDiscrepancies,
  useGetReconcileProposals,
  useGetPendingCorrections,
  useGetUberReportCoverage,
  useGetUncertainShifts,
  useGetShiftDrafts,
  useReleaseShiftDraft,
  useFixShiftDates,
} from '../hooks/backend/shifts'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import { Chip, type ChipTone } from '../components/interviews/Chip'
import { ReconcileDialog } from '../components/shifts/ReconcileDialog'
import { NoProposalChip } from '../components/shifts/NoProposalChip'
import { PendingCorrectionsQueue } from '../components/shifts/PendingCorrectionsQueue'
import {
  UberReportQueue,
  type UberReportDay,
  type UberReportRow,
} from '../components/shifts/UberReportQueue'
import {
  ShiftDraftQueue,
  type ShiftDraftRow,
  type ShiftDateMismatchRow,
} from '../components/shifts/ShiftDraftQueue'
import { KmDiscrepancyQueue, type KmDiscrepancyRow } from '../components/shifts/KmDiscrepancyQueue'
import {
  UncertainShiftQueue,
  type UncertainShift,
} from '../components/shifts/UncertainShiftQueue'
import {
  QueueFilters,
  BASE_FILTERS,
  SEVERITY_FILTERS,
  matchesSeverity,
  type QueueFilter,
  type SeverityFilter,
} from '../components/shifts/QueueFilters'
import {
  proposalCategory,
  windowLabel,
  type ReconcileProposal,
  type PendingCorrection,
} from '../components/shifts/reconcile'
import { formatZar, formatNum, formatDate } from '../utils/interviews'

// ---------- Row types (shapes confirmed against the Supabase views) ----------

type DuplicateSlotRow = {
  shift_id: string
  registration_number: string | null
  shift_date: string | null
  shift_window: string | null
  driver_id: string | null
  driver_name: string | null
  shift_status: string | null
  shift_submitted_at: string | null
  declared_km: number | string | null
  uber_gross_zar: number | string | null
  earnings_as_paid: number | string | null
  drivers_in_slot: number | string | null
  has_part_a: boolean | null
  exposure: number | string | null
}

type VehicleMismatchRow = {
  shift_id: string
  driver_id: string | null
  shift_date: string | null
  shift_window: string | null
  claimed_vehicle: string | null
  expected_vehicle: string | null
  prev_date: string | null
  next_date: string | null
  span_days: number | string | null
  claimed_vehicle_trips_nearby: number | string | null
  expected_vehicle_trips_nearby: number | string | null
  confidence: string | null
}

type Tab =
  | 'duplicate'
  | 'mismatch'
  | 'clashes'
  | 'km'
  | 'pending'
  | 'reports'
  | 'drafts'
  | 'unsure'

type VehicleSlotClashRow = {
  plate: string | null
  shift_date: string | null
  shift_window: string | null
  shift_id: string
  driver_id: string | null
  driver_name: string | null
  shift_status: string | null
  period: string
  trips_this_window: number | string | null
  plate_this_window: string | null
  trips_other_window: number | string | null
  plate_other_window: string | null
  holds_other_window_same_vehicle: boolean | null
  has_driver_form: boolean | null
  earnings_as_paid: number | string | null
  pay_locked: boolean | null
  classification: string
  suggested_action: string | null
}

function clashTone(classification: string): ChipTone {
  if (classification === 'EVIDENCED_HERE') return 'green'
  if (classification === 'FROZEN_PRE_MARCH') return 'neutral'
  if (classification === 'SAME_WINDOW_SPILLOVER_DUPLICATE') return 'amber'
  return 'red'
}

// ---------- Display helpers ----------

function humanize(value: string | null | undefined): string {
  if (!value) return '—'
  const spaced = value.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function evidence(row: DuplicateSlotRow): { tone: ChipTone; label: string } {
  if (row.has_part_a === true) return { tone: 'green', label: 'form' }
  if (row.shift_submitted_at != null) return { tone: 'amber', label: 'submitted' }
  return { tone: 'red', label: 'no evidence' }
}

function confidenceTone(confidence: string | null | undefined): ChipTone {
  if (confidence === 'high') return 'red'
  if (confidence === 'medium') return 'amber'
  return 'neutral'
}

// Whether a shift's proposal falls into the selected filter bucket. A shift
// with no proposal at all counts as Blocked.
function matchesReconcileFilter(
  proposal: ReconcileProposal | undefined,
  filter: QueueFilter,
): boolean {
  if (filter === 'all') return true
  return proposalCategory(proposal) === filter
}

// One visual group per (car, date, window) slot.
type SlotGroup = {
  key: string
  registration_number: string | null
  shift_date: string | null
  shift_window: string | null
  exposure: number | string | null
  drivers: DuplicateSlotRow[]
}

function groupSlots(rows: DuplicateSlotRow[]): SlotGroup[] {
  const map = new Map<string, SlotGroup>()
  for (const r of rows) {
    const key = `${r.registration_number}|${r.shift_date}|${r.shift_window}`
    let g = map.get(key)
    if (!g) {
      g = {
        key,
        registration_number: r.registration_number,
        shift_date: r.shift_date,
        shift_window: r.shift_window,
        exposure: r.exposure,
        drivers: [],
      }
      map.set(key, g)
    }
    g.drivers.push(r)
  }
  return Array.from(map.values())
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'duplicate', label: 'Duplicate slots' },
  { id: 'mismatch', label: 'Wrong vehicle' },
  { id: 'clashes', label: 'Vehicle slot clashes' },
  { id: 'km', label: 'Kilometre gap' },
  { id: 'pending', label: 'Pending corrections' },
  { id: 'reports', label: 'Uber reports' },
  { id: 'drafts', label: 'Drafts' },
  { id: 'unsure', label: 'Unsure' },
]

const EXPLANATIONS: Record<Tab, string> = {
  duplicate:
    'One car, one date, one window, two or more drivers. Only one person physically had the car. Rows with no form and no submitted timestamp are the likely phantoms.',
  mismatch:
    'The driver’s previous and next shift both used one vehicle, but this shift claims a different one. Most likely a mis-tap on the vehicle dropdown.',
  clashes:
    'Every live shift record that sits in a vehicle slot claimed by more than one driver, one row per claimant, with the Uber evidence that decides it. A vehicle slot is one vehicle, one date, one window: only one driver can physically hold the car, so two names in a slot is a record keeping fault, not a judgement on either driver. Rows before March are reported only and are never actioned, because Uber Fleet has no trip activity to test them against. This tab is read-only for now: it shows what the evidence supports, not a button to act on it.',
  km: 'Shadow mode. Every shift is recorded, nothing is enforced. A positive percentage means the driver declared less than the vehicle moved. Single-shift measurement noise runs to roughly plus or minus 17 percent, so treat one row as a question rather than a finding, and read it next to the partner’s shift on the same vehicle and day.',
  pending:
    'Corrections that have been proposed and are waiting for a person to approve and apply them. Review one at a time, or select several and approve or close them as a batch — each row still applies in its own subtransaction, so one refusal never rolls back the rest.',
  drafts:
    'A shift a driver submitted is a draft until Uber or the tracker agrees with it. Until 26 August the validator promoted a shift to validated whenever nothing actively contradicted it, and a check that finds NO evidence contradicts nothing, so a shift with no Uber evidence at all, including one on the wrong date, went into pay. This tab is what the gate holds instead, and the dates Uber contradicts. Read the coverage figure before acting: where the Uber report for a whole date never arrived, those drivers worked and their dates are almost certainly right, and the gap is Fleetly\u2019s to close rather than theirs to answer for. The count beside the tab is shifts held.',
  unsure:
    'Shifts the private trip and Uber validation work could not settle either way, grouped by what makes them uncertain and what would settle each group. The first three groups are private trips above the R1,000 review ceiling: that money is held OFF an owner\u2019s statement until a person looks, and a hold that shows nowhere is indistinguishable from an oversight. Read the earnings per kilometre before the size of the gap: a car earning at a normal rate on Uber has no missing revenue for a private trip to explain, which is what a mistyped thousands digit looks like. The last two groups are not judgements, they are a load or a link: a driver with no Uber identity on file, and shifts whose submitted figure was overwritten before it was kept. This tab is read-only.',
  reports:
    'Which Uber reports are outstanding per shift date, and what that is holding up. A date releases its driver pay and position email only when every required report is both parsed into the database and registered as submitted, so a date can hold every figure correctly and still send nothing. The count beside the tab is dates with a required report outstanding. Two columns sit deliberately beside the report state: shifts validated, and Uber earnings belonging to a driver with no shift record on that date. Reports complete is not the same claim as the day being safe to pay from. This tab is read-only: registering a report is what releases a driver-facing pay email, so it stays a deliberate act rather than a button next to a table.',
}

export default function ShiftValidation() {
  const { loading: accessLoading, canView, caption } = useCapabilities()
  const [tab, setTab] = useState<Tab>('duplicate')

  // Filter choice per tab, persisted in component state so switching tabs and
  // coming back preserves the selection.
  const [dupFilter, setDupFilter] = useState<QueueFilter>('all')
  const [misFilter, setMisFilter] = useState<QueueFilter>('all')
  const [kmFilter, setKmFilter] = useState<QueueFilter>('all')
  const [pendingFilter, setPendingFilter] = useState<QueueFilter>('all')

  // Severity is a second, independent filter dimension on the pending,
  // duplicate and wrong-vehicle tabs, persisted per tab alongside the status
  // filter. The km tab has no severity split.
  const [dupSeverity, setDupSeverity] = useState<SeverityFilter>('all')
  const [misSeverity, setMisSeverity] = useState<SeverityFilter>('all')
  const [pendingSeverity, setPendingSeverity] = useState<SeverityFilter>('all')

  const duplicates = useGetDuplicateSlots()
  const mismatches = useGetVehicleMismatches()
  const clashes = useGetVehicleSlotClashes()
  const kmGaps = useGetKmDiscrepancies()
  const reconcile = useGetReconcileProposals()
  const pending = useGetPendingCorrections()
  const uberReports = useGetUberReportCoverage()
  const uncertain = useGetUncertainShifts()
  const shiftDrafts = useGetShiftDrafts()
  const releaseDraft = useReleaseShiftDraft()
  const fixDates = useFixShiftDates()
  const dupTrigger = duplicates.trigger
  const misTrigger = mismatches.trigger
  const clashesTrigger = clashes.trigger
  const kmTrigger = kmGaps.trigger
  const reconcileTrigger = reconcile.trigger
  const pendingTrigger = pending.trigger
  const uberReportsTrigger = uberReports.trigger
  const uncertainTrigger = uncertain.trigger
  const draftsTrigger = shiftDrafts.trigger

  const load = useCallback(() => {
    dupTrigger({}, { skipCache: true })
    misTrigger({}, { skipCache: true })
    clashesTrigger({}, { skipCache: true })
    kmTrigger({}, { skipCache: true })
    reconcileTrigger({}, { skipCache: true })
    pendingTrigger({}, { skipCache: true })
    uberReportsTrigger({}, { skipCache: true })
    draftsTrigger({}, { skipCache: true })
    uncertainTrigger({}, { skipCache: true })
  }, [
    dupTrigger,
    misTrigger,
    clashesTrigger,
    kmTrigger,
    reconcileTrigger,
    pendingTrigger,
    uberReportsTrigger,
    draftsTrigger,
    uncertainTrigger,
  ])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const dupRows = (duplicates.data as DuplicateSlotRow[] | undefined) ?? []
  const misRows = (mismatches.data as VehicleMismatchRow[] | undefined) ?? []
  const clashRows = (clashes.data as VehicleSlotClashRow[] | undefined) ?? []
  const kmRows = (kmGaps.data as KmDiscrepancyRow[] | undefined) ?? []
  const reconcileRows = (reconcile.data as ReconcileProposal[] | undefined) ?? []
  const pendingRows = (pending.data as PendingCorrection[] | undefined) ?? []
  const uberPayload = uberReports.data as
    | { days?: UberReportDay[]; reports?: UberReportRow[] }
    | undefined
  const uberDays = uberPayload?.days ?? []
  const uberReportRows = uberPayload?.reports ?? []

  const draftPayload = shiftDrafts.data as
    | {
        drafts?: ShiftDraftRow[]
        dateMismatches?: ShiftDateMismatchRow[]
        gateOn?: boolean
        canAct?: boolean
      }
    | undefined
  const uncertainRows = (uncertain.data as UncertainShift[] | undefined) ?? []
  const draftRows = draftPayload?.drafts ?? []
  const dateMismatchRows = draftPayload?.dateMismatches ?? []

  // The tab count is dates with a required report outstanding, not slots: an
  // operator acts on a day, and four missing reports on one day is one problem.
  const uberOutstandingDates = useMemo(
    () => uberDays.filter((d) => Number(d.outstanding_required ?? 0) > 0).length,
    [uberDays],
  )

  const slotGroups = useMemo(() => groupSlots(dupRows), [dupRows])

  // Each row carries its own category total, so summing one row per category
  // gives the true size of the tab even though the list itself is capped.
  const uncertainBucketTotal = useMemo(() => {
    const seen = new Map<number, number>()
    for (const r of uncertainRows) seen.set(r.sort_order, r.bucket_shifts)
    let total = 0
    for (const n of seen.values()) total += n
    return total
  }, [uncertainRows])

  // Proposal lookup keyed by shift_id, joined into every queue.
  const proposalByShift = useMemo(() => {
    const map = new Map<string, ReconcileProposal>()
    for (const p of reconcileRows) map.set(p.shift_id, p)
    return map
  }, [reconcileRows])

  const counts: Record<Tab, number> = {
    duplicate: dupRows.length,
    mismatch: misRows.length,
    clashes: clashRows.length,
    km: kmRows.length,
    pending: pendingRows.length,
    reports: uberOutstandingDates,
    drafts: draftRows.length,
    // The count is the WHOLE category, not the capped list the handler returns.
    // A badge that reads 200 when 1,186 shifts are unsettled understates the
    // problem, which is the one thing this tab exists to stop.
    unsure: uncertainRows.length > 0 ? uncertainBucketTotal : 0,
  }

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Shift Validation is available to admins only.
          </p>
          <p className="text-xs text-muted-foreground">
            Contact an admin if you need access to shift validation data.
          </p>
        </div>
      </div>
    )
  }

  const active =
    tab === 'duplicate'
      ? duplicates
      : tab === 'mismatch'
        ? mismatches
        : tab === 'clashes'
          ? clashes
          : tab === 'km'
            ? kmGaps
            : tab === 'reports'
              ? uberReports
              : tab === 'drafts'
                ? shiftDrafts
                : tab === 'unsure'
                  ? uncertain
                  : pending

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <CalendarClock className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Shift Validation</h1>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
        <p className="mt-2 text-sm font-medium text-foreground">
          <span className="tabular-nums">{counts.duplicate}</span> duplicate slot
          {counts.duplicate === 1 ? '' : 's'} ·{' '}
          <span className="tabular-nums">{counts.mismatch}</span> wrong vehicle ·{' '}
          <span className="tabular-nums">{counts.km}</span> kilometre gap
          {counts.km === 1 ? '' : 's'}
        </p>

        {/* Tabs */}
        <div className="mt-3 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
                tab === t.id
                  ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
                {counts[t.id]}
              </span>
            </button>
          ))}
        </div>

        <p className="mt-2 max-w-3xl text-xs leading-relaxed text-muted-foreground">
          {EXPLANATIONS[tab]}
        </p>
      </div>

      {/* Content */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {active.error ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{active.error}</span>
          </div>
        ) : active.loading && counts[tab] === 0 ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading…
          </div>
        ) : tab === 'duplicate' ? (
          <DuplicateQueue
            groups={slotGroups}
            proposals={proposalByShift}
            filter={dupFilter}
            onFilterChange={setDupFilter}
            severity={dupSeverity}
            onSeverityChange={setDupSeverity}
            onProposed={load}
          />
        ) : tab === 'mismatch' ? (
          <MismatchQueue
            rows={misRows}
            proposals={proposalByShift}
            filter={misFilter}
            onFilterChange={setMisFilter}
            severity={misSeverity}
            onSeverityChange={setMisSeverity}
            onProposed={load}
          />
        ) : tab === 'clashes' ? (
          <VehicleSlotClashQueue rows={clashRows} />
        ) : tab === 'drafts' ? (
          <ShiftDraftQueue
            drafts={draftRows}
            dateMismatches={dateMismatchRows}
            canAct={draftPayload?.canAct === true}
            gateOn={draftPayload?.gateOn === true}
            busy={releaseDraft.loading === true || fixDates.loading === true}
            onRelease={(shiftId, reason) => {
              void releaseDraft
                .trigger({ shift_id: shiftId, reason }, { skipCache: true })
                .then(() => draftsTrigger({}, { skipCache: true }))
            }}
            onFixDates={() => {
              void fixDates
                .trigger(
                  { from_date: '2026-07-01', to_date: null, apply: true },
                  { skipCache: true },
                )
                .then(() => draftsTrigger({}, { skipCache: true }))
            }}
          />
        ) : tab === 'unsure' ? (
          <UncertainShiftQueue rows={uncertainRows} />
        ) : tab === 'reports' ? (
          <UberReportQueue days={uberDays} reports={uberReportRows} />
        ) : tab === 'km' ? (
          <KmDiscrepancyQueue
            rows={kmRows}
            proposals={proposalByShift}
            filter={kmFilter}
            onFilterChange={setKmFilter}
            onChanged={load}
          />
        ) : (
          <PendingCorrectionsQueue
            rows={pendingRows}
            proposals={proposalByShift}
            filter={pendingFilter}
            onFilterChange={setPendingFilter}
            severity={pendingSeverity}
            onSeverityChange={setPendingSeverity}
            onApplied={load}
          />
        )}
      </div>
    </div>
  )
}

// ---------- Queue 1: Duplicate slots ----------

function DuplicateQueue({
  groups,
  proposals,
  filter,
  onFilterChange,
  severity,
  onSeverityChange,
  onProposed,
}: {
  groups: SlotGroup[]
  proposals: Map<string, ReconcileProposal>
  filter: QueueFilter
  onFilterChange: (next: QueueFilter) => void
  severity: SeverityFilter
  onSeverityChange: (next: SeverityFilter) => void
  onProposed: () => void
}) {
  // Severity comes from the joined proposal's correction_type. A driver row
  // with no proposal only appears under "All severities".
  const severityOf = (shiftId: string) => proposals.get(shiftId)?.correction_type ?? null

  // Status chip counts are over individual driver rows (not slot groups) and
  // respect the current severity.
  const counts = useMemo(() => {
    const c: Partial<Record<QueueFilter, number>> = {}
    const allDrivers = groups.flatMap((g) => g.drivers)
    for (const f of BASE_FILTERS) {
      c[f.id] = allDrivers.filter(
        (d) =>
          matchesReconcileFilter(proposals.get(d.shift_id), f.id) &&
          matchesSeverity(severityOf(d.shift_id), severity),
      ).length
    }
    return c
  }, [groups, proposals, severity])

  // Severity chip counts respect the current status filter.
  const severityCounts = useMemo(() => {
    const c: Partial<Record<SeverityFilter, number>> = {}
    const allDrivers = groups.flatMap((g) => g.drivers)
    for (const f of SEVERITY_FILTERS) {
      c[f.id] = allDrivers.filter(
        (d) =>
          matchesReconcileFilter(proposals.get(d.shift_id), filter) &&
          matchesSeverity(severityOf(d.shift_id), f.id),
      ).length
    }
    return c
  }, [groups, proposals, filter])

  // Filter drivers within each group by both dimensions, then drop empty groups.
  const visibleGroups = useMemo(() => {
    return groups
      .map((g) => ({
        ...g,
        drivers: g.drivers.filter(
          (d) =>
            matchesReconcileFilter(proposals.get(d.shift_id), filter) &&
            matchesSeverity(severityOf(d.shift_id), severity),
        ),
      }))
      .filter((g) => g.drivers.length > 0)
  }, [groups, proposals, filter, severity])

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <QueueFilters
          filters={BASE_FILTERS}
          value={filter}
          counts={counts}
          onChange={onFilterChange}
        />
        <QueueFilters
          filters={SEVERITY_FILTERS}
          value={severity}
          counts={severityCounts}
          onChange={onSeverityChange}
        />
      </div>

      {visibleGroups.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
          <Copy className="h-8 w-8 opacity-40" />
          No duplicate slots match this filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Declared km</TableHead>
                <TableHead className="text-right">Uber gross</TableHead>
                <TableHead className="text-right">Earnings</TableHead>
                <TableHead>Evidence</TableHead>
                <TableHead className="text-right">Reconcile</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleGroups.map((g) => (
                <Fragment key={g.key}>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableCell colSpan={7} className="py-2">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        <span className="flex items-center gap-1.5 font-mono text-sm font-semibold text-foreground">
                          <Car className="h-4 w-4 text-[hsl(var(--brand))]" />
                          {g.registration_number || '—'}
                        </span>
                        <span className="text-muted-foreground">{formatDate(g.shift_date)}</span>
                        <span className="text-muted-foreground">{windowLabel(g.shift_window)}</span>
                        <span className="text-muted-foreground">
                          {g.drivers.length} drivers contesting
                        </span>
                        <span className="ml-auto font-medium text-foreground">
                          Exposure {formatZar(g.exposure)}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                  {g.drivers.map((d) => {
                    const ev = evidence(d)
                    return (
                      <TableRow key={d.shift_id}>
                        <TableCell className="font-medium text-foreground">
                          {d.driver_name || 'Unnamed driver'}
                        </TableCell>
                        <TableCell>
                          <Chip tone="neutral">{humanize(d.shift_status)}</Chip>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {formatNum(d.declared_km, 0)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {formatZar(d.uber_gross_zar)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-foreground">
                          {formatZar(d.earnings_as_paid)}
                        </TableCell>
                        <TableCell>
                          <Chip tone={ev.tone}>{ev.label}</Chip>
                        </TableCell>
                        <TableCell className="text-right">
                          {proposals.has(d.shift_id) ? (
                            <ReconcileDialog
                              proposal={proposals.get(d.shift_id)!}
                              onProposed={onProposed}
                            />
                          ) : (
                            <NoProposalChip tooltip="This driver's own history supports their claim. The proposal sits on the other driver in this slot." />
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

// ---------- Queue 2: Wrong vehicle ----------

function MismatchQueue({
  rows,
  proposals,
  filter,
  onFilterChange,
  severity,
  onSeverityChange,
  onProposed,
}: {
  rows: VehicleMismatchRow[]
  proposals: Map<string, ReconcileProposal>
  filter: QueueFilter
  onFilterChange: (next: QueueFilter) => void
  severity: SeverityFilter
  onSeverityChange: (next: SeverityFilter) => void
  onProposed: () => void
}) {
  // Severity comes from the joined proposal's correction_type. A row with no
  // proposal only appears under "All severities".
  const severityOf = (shiftId: string) => proposals.get(shiftId)?.correction_type ?? null

  const counts = useMemo(() => {
    const c: Partial<Record<QueueFilter, number>> = {}
    for (const f of BASE_FILTERS) {
      c[f.id] = rows.filter(
        (r) =>
          matchesReconcileFilter(proposals.get(r.shift_id), f.id) &&
          matchesSeverity(severityOf(r.shift_id), severity),
      ).length
    }
    return c
  }, [rows, proposals, severity])

  const severityCounts = useMemo(() => {
    const c: Partial<Record<SeverityFilter, number>> = {}
    for (const f of SEVERITY_FILTERS) {
      c[f.id] = rows.filter(
        (r) =>
          matchesReconcileFilter(proposals.get(r.shift_id), filter) &&
          matchesSeverity(severityOf(r.shift_id), f.id),
      ).length
    }
    return c
  }, [rows, proposals, filter])

  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          matchesReconcileFilter(proposals.get(r.shift_id), filter) &&
          matchesSeverity(severityOf(r.shift_id), severity),
      ),
    [rows, proposals, filter, severity],
  )

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <QueueFilters
          filters={BASE_FILTERS}
          value={filter}
          counts={counts}
          onChange={onFilterChange}
        />
        <QueueFilters
          filters={SEVERITY_FILTERS}
          value={severity}
          counts={severityCounts}
          onChange={onSeverityChange}
        />
      </div>

      {visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
          <Car className="h-8 w-8 opacity-40" />
          No vehicle mismatches match this filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Shift</TableHead>
                <TableHead>Claimed</TableHead>
                <TableHead>Expected</TableHead>
                <TableHead className="text-center">Cartrack trips (claimed / expected)</TableHead>
                <TableHead>Neighbouring shifts</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead className="text-right">Reconcile</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((r) => {
                const claimed = Number(r.claimed_vehicle_trips_nearby ?? 0)
                const expected = Number(r.expected_vehicle_trips_nearby ?? 0)
                // The tell: claimed vehicle didn't move nearby but the expected one did.
                const claimedWrong = claimed === 0 && expected > 0
                return (
                  <TableRow key={r.shift_id}>
                    <TableCell>
                      <div className="text-sm font-medium text-foreground">
                        {formatDate(r.shift_date)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {windowLabel(r.shift_window)}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-foreground">
                      {r.claimed_vehicle || '—'}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-foreground">
                      {r.expected_vehicle || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-2 tabular-nums">
                        <span
                          className={cn(
                            'rounded-md border px-2 py-0.5 text-sm font-medium',
                            claimedWrong
                              ? 'border-rose-200 bg-rose-100 text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200'
                              : 'border-border bg-background text-foreground',
                          )}
                          title="Trips by the claimed vehicle near this shift"
                        >
                          {claimed}
                        </span>
                        <span className="text-muted-foreground">/</span>
                        <span
                          className={cn(
                            'rounded-md border px-2 py-0.5 text-sm font-medium',
                            claimedWrong
                              ? 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
                              : 'border-border bg-background text-foreground',
                          )}
                          title="Trips by the expected vehicle near this shift"
                        >
                          {expected}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDate(r.prev_date)} → {formatDate(r.next_date)}
                      <span className="ml-1 text-muted-foreground/70">({r.span_days ?? '—'}d)</span>
                    </TableCell>
                    <TableCell>
                      <Chip tone={confidenceTone(r.confidence)}>{humanize(r.confidence)}</Chip>
                    </TableCell>
                    <TableCell className="text-right">
                      {proposals.has(r.shift_id) ? (
                        <ReconcileDialog
                          proposal={proposals.get(r.shift_id)!}
                          onProposed={onProposed}
                        />
                      ) : (
                        <NoProposalChip tooltip="No automated fix exists for this shift. It needs human investigation." />
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

// ---------- Queue: Vehicle slot clashes (read-only) ----------
//
// No action is offered here: the brief for this view gave no apply/deny path,
// and most rows either can never be actioned (pre March, no Uber activity to
// test against) or move pay if touched (pay_locked). It exists so a person can
// see what the evidence supports before deciding anything by hand.

function VehicleSlotClashQueue({ rows }: { rows: VehicleSlotClashRow[] }) {
  const [onlyResolvable, setOnlyResolvable] = useState(true)

  const visible = useMemo(
    () => (onlyResolvable ? rows.filter((r) => r.period === 'resolvable') : rows),
    [rows, onlyResolvable],
  )

  const resolvableCount = useMemo(() => rows.filter((r) => r.period === 'resolvable').length, [rows])

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOnlyResolvable(true)}
          className={cn(
            'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            onlyResolvable
              ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
              : 'border-border bg-background text-muted-foreground hover:text-foreground',
          )}
        >
          Resolvable
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
            {resolvableCount}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setOnlyResolvable(false)}
          className={cn(
            'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            !onlyResolvable
              ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
              : 'border-border bg-background text-muted-foreground hover:text-foreground',
          )}
        >
          All, including pre March
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
            {rows.length}
          </span>
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
          <Car className="h-8 w-8 opacity-40" />
          No vehicle slot clashes match this filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plate</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead className="text-center">Trips (this / other window)</TableHead>
                <TableHead>Classification</TableHead>
                <TableHead>Suggested action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((r) => (
                <TableRow key={r.shift_id}>
                  <TableCell className="font-mono text-sm text-foreground">{r.plate || '-'}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium text-foreground">{formatDate(r.shift_date)}</div>
                    <div className="text-xs text-muted-foreground">{windowLabel(r.shift_window)}</div>
                    {r.period === 'frozen_pre_march' && (
                      <div className="mt-0.5">
                        <Chip tone="neutral">Pre March, reported only</Chip>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-foreground">{r.driver_name || '-'}</TableCell>
                  <TableCell className="text-center tabular-nums text-muted-foreground">
                    {formatNum(r.trips_this_window, 0)} / {formatNum(r.trips_other_window, 0)}
                  </TableCell>
                  <TableCell>
                    <Chip tone={clashTone(r.classification)}>{humanize(r.classification)}</Chip>
                    {r.pay_locked && (
                      <div className="mt-1 text-[11px] text-muted-foreground">Pay locked</div>
                    )}
                  </TableCell>
                  <TableCell className="max-w-md text-xs leading-relaxed text-muted-foreground">
                    {r.suggested_action || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
